import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Bot, ShieldCheck, Zap, ArrowRight, Activity, Wallet, TrendingUp,
  Lock, Layers, CircleCheck,
} from 'lucide-react';
import { PublicNav, Footer } from '../../components/PublicChrome';
import { SectionTitle, Panel } from '../../components/Primitives';
import { Sparkline } from '../../components/Charts';
import { useI18n } from '../../i18n/I18nContext';
import { api, fmtUsd } from '../../lib/api';
import { IntroSequence } from '../../components/IntroSequence';
import { LogoShowcase } from '../../components/LogoShowcase';


function MarketStrip() {
  const [coins, setCoins] = useState([]);
  useEffect(() => {
    api.get('/markets/top?limit=10').then(({ data }) => setCoins(data.coins)).catch(() => {});
  }, []);
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="landing-market-strip">
      {coins.slice(0, 5).map((c, i) => {
        const up = (c.price_change_percentage_24h || 0) >= 0;
        return (
          <motion.div key={c.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * i }}
            className="vj-card p-3">
            <div className="flex items-center gap-2">
              <img src={c.image} alt="" className="w-5 h-5" />
              <span className="text-xs font-semibold">{c.symbol}</span>
            </div>
            <div className="font-mono text-sm mt-2">{fmtUsd(c.current_price, c.current_price < 1 ? 4 : 2)}</div>
            <div className={`text-xs font-mono ${up ? 'text-vj-neon' : 'text-vj-red'}`}>
              {up ? '+' : ''}{(c.price_change_percentage_24h || 0).toFixed(2)}%
            </div>
            <div className="mt-1"><Sparkline data={c.sparkline} positive={up} width={120} height={28} /></div>
          </motion.div>
        );
      })}
    </div>
  );
}

const FLOAT = ['₿', 'Ξ', '◎', '₮', '✕'];

export default function Landing() {
  const { t } = useI18n();
  const navigate = useNavigate();

  const steps = [t('steps.1'), t('steps.2'), t('steps.3'), t('steps.4'), t('steps.5')];
  const features = [
    { icon: Bot, title: 'S.I. Bot Engine', desc: 'A modular trading workspace with transparent operational status and paper / live execution modes.' },
    { icon: Activity, title: 'Live Market Data', desc: 'Top-10 cryptocurrencies ranked by real CoinGecko market data with candlestick charts.' },
    { icon: ShieldCheck, title: 'Verified Deposits', desc: 'USDT BEP20 deposits verified on-chain via BscScan with confirmation thresholds.' },
    { icon: Wallet, title: 'Authoritative Ledger', desc: 'Every balance comes from a fixed-precision backend ledger — never guessed, never inflated.' },
    { icon: Layers, title: 'Automation Center', desc: 'Automatic, manual, and hybrid controls for every operational workflow.' },
    { icon: Lock, title: 'Role-Based Security', desc: 'Least-privilege admin roles, audit logs, and emergency pause controls.' },
  ];

  return (
    <div className="min-h-screen bg-vj-obsidian text-vj-silver overflow-x-clip">
      <IntroSequence />
      <PublicNav />

      {/* Hero */}
      <section className="relative pt-32 lg:pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0 vj-grid-bg opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-vj-obsidian/40 to-vj-obsidian pointer-events-none" />
        {FLOAT.map((s, i) => (
          <div key={i} className="absolute text-vj-neon/20 font-display text-5xl vj-float pointer-events-none select-none"
            style={{ top: `${15 + i * 14}%`, left: `${8 + i * 17}%`, animationDelay: `${i * 0.8}s` }}>{s}</div>
        ))}

        <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
          <div className="max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-vj-neon/30 bg-vj-neon/5 text-xs text-vj-neon mb-6">
              <Zap size={13} /> Automation-first global trading
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              <span className="vj-gradient-text">{t('hero.headline')}</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 text-base lg:text-lg text-vj-grey max-w-2xl">
              {t('hero.sub')}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => navigate('/register')} data-testid="hero-get-started"
                className="group h-12 px-7 rounded-xl bg-vj-neon text-vj-obsidian font-semibold inline-flex items-center gap-2 hover:-translate-y-0.5 transition-all vj-glow-green">
                {t('nav.getStarted')} <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button onClick={() => navigate('/markets')} data-testid="hero-explore-markets"
                className="h-12 px-7 rounded-xl border border-vj-silver/20 text-vj-silver font-medium hover:border-vj-neon/50 transition-all">
                {t('nav.exploreMarkets')}
              </button>
              <button onClick={() => navigate('/si-bot')} data-testid="hero-discover-bot"
                className="h-12 px-7 rounded-xl border border-vj-gold/30 text-vj-gold font-medium hover:bg-vj-gold/10 transition-all">
                {t('hero.discoverBot')}
              </button>
            </motion.div>
          </div>
          <LogoShowcase />
          </div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.35 }}
            className="mt-14 lg:mt-20">
            <MarketStrip />
          </motion.div>
        </div>
      </section>

      {/* S.I. Bot intro */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16 overflow-hidden">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionTitle eyebrow={t('section.sibot')} title="Super Intelligence Trading System"
              desc="The S.I. Bot is a sophisticated trading workspace with market intelligence panels, transparent operational status, and clearly labelled execution modes. It never claims predictive certainty or guaranteed profits." />
            <div className="space-y-3">
              {['Market monitoring & strategy evaluation', 'Paper trading with full P&L accounting', 'Risk limits, exposure caps & emergency stop', 'Live exchange adapter (enabled once configured)'].map((f) => (
                <div key={f} className="flex items-center gap-3 text-sm text-vj-silver/90">
                  <CircleCheck size={16} className="text-vj-neon shrink-0" /> {f}
                </div>
              ))}
            </div>
          </div>
          <div className="relative grid place-items-center h-80">
            <div className="absolute w-64 h-64 rounded-full bg-vj-neon/10 blur-3xl" />
            <div className="relative w-56 h-56 rounded-full border border-vj-neon/40 vj-pulse grid place-items-center">
              <div className="w-40 h-40 rounded-full border border-vj-gold/40 grid place-items-center bg-vj-charcoal/60 backdrop-blur">
                <Bot size={52} className="text-vj-neon vj-text-glow" />
              </div>
            </div>
            <div className="absolute inset-0 vj-spin-slow pointer-events-none">
              {[0, 90, 180, 270].map((deg) => (
                <div key={deg} className="absolute w-2.5 h-2.5 rounded-full bg-vj-gold" style={{ top: '50%', left: '50%', transform: `rotate(${deg}deg) translateX(120px)` }} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16">
        <SectionTitle center eyebrow="Process" title={t('section.howItWorks')}
          desc="Five transparent steps from registration to withdrawal. Deposited funds, allocated capital, trading performance, and withdrawable balances stay clearly separated." />
        <div className="grid md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="vj-card p-5 relative">
              <div className="w-9 h-9 rounded-lg bg-vj-neon/10 text-vj-neon font-display font-bold grid place-items-center mb-4">{i + 1}</div>
              <p className="text-sm text-vj-silver/90">{s}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16">
        <SectionTitle eyebrow="Capabilities" title={t('section.features')} />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="vj-card p-6 group">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-vj-neon/20 to-vj-gold/10 grid place-items-center mb-4 group-hover:scale-105 transition-transform">
                <f.icon size={20} className="text-vj-neon" />
              </div>
              <h3 className="font-display font-semibold text-foreground mb-2">{f.title}</h3>
              <p className="text-sm text-vj-grey">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Security */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16">
        <div className="vj-card p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-vj-emerald/10 blur-3xl rounded-full" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <SectionTitle eyebrow={t('section.security')} title="Security & Blockchain Infrastructure"
                desc="Secure password hashing, server-side authorization, audit logging, idempotent on-chain event processing, and an immutable, fixed-precision financial ledger." />
              <button onClick={() => navigate('/how-it-works')} className="text-sm text-vj-neon inline-flex items-center gap-2 hover:gap-3 transition-all">
                See how deposits are verified <ArrowRight size={15} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[['BEP20', 'USDT network'], ['Chain 56', 'BNB Smart Chain'], ['15', 'confirmations'], ['BscScan', 'verification']].map(([a, b]) => (
                <div key={b} className="vj-card p-5 text-center">
                  <div className="font-display text-2xl font-bold text-vj-gold">{a}</div>
                  <div className="text-xs text-vj-grey mt-1">{b}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Risk */}
      <section className="max-w-4xl mx-auto px-4 lg:px-8 py-10">
        <div className="rounded-xl border border-vj-red/30 bg-vj-red/5 p-6">
          <h3 className="font-display font-semibold text-vj-red mb-2 flex items-center gap-2"><TrendingUp size={18} /> {t('section.risk')}</h3>
          <p className="text-sm text-vj-grey">
            Trading cryptocurrency involves substantial risk of loss and is not suitable for every investor. VEYJEX does not provide guaranteed returns, and paper-trading or admin-recorded results are never presented as real executed exchange trades. Only deposit funds you can afford to lose.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
